// Map #2 — Shattered Metropolis (sim half). An abandoned energy-age city at dusk, broken long ago by a catastrophe that left
// glowing scars in the streets. Gameplay archetypes, reskinned by render.ts:
//   spire   = leaning tower blocks (they lean and sink as they take damage, then come down)
//   boulder = vehicle wrecks jammed against concrete barricades
//   rock    = loose concrete debris
//   tree    = streetlights (flicker on at dusk) and scrubby trees gone wild in their planters
//   bush    = weed clumps and overgrowth
// Hazards (light, telegraphed, hit the hero AND the foes):
//   collapse — a tower sheds a slab of its upper floors onto the street beside it: a long shadow + cracks + falling grit for
//              2.8 s, then it lands (22 damage, crushes ordinary foes, dents bosses, leaves a crater)
//   cable    — downed power lines across / along the boulevards: they crackle for 2.2 s, then arc for 6 s (4 HP/s; foes
//              lose 6% max HP/s), every ~25 s
//   smoke    — a burning wreck flares (3 s) and pours smoke for 20 s: visibility at the hero drops by 45%, everyone in it is
//              a little slower
// Pure: no three, no DOM, no unseeded randomness (check:ip).
import type { HazardDef } from '../../core/hazards';
import type { MapCoreDef } from '../../core/mapdef';
import type { Prop, VariantDef } from '../../core/terrain';
import { BUSH_VARIANTS, CLASSIC_HILLS, TREE_VARIANTS } from '../../core/terrain';
import type { Rng } from '../../core/util';
import { CITY, GRID_HEADING, toWorld } from './coreCity';

/** tree archetype variants (index order = the renderer's shapes): a streetlight, then three scrub trees in old planters */
export const CITY_TREES: VariantDef[] = [
  { name: 'lamp', r: 0.34, s: [0.95, 1.08], h: [0.96, 1.06], hp: 0.7, w: 6 },
  { ...TREE_VARIANTS[0], name: 'scrub', s: [0.75, 1.0], h: [0.78, 0.92], w: 2 },
  { ...TREE_VARIANTS[4], name: 'sapling', w: 3 },
  { ...TREE_VARIANTS[2], name: 'round', s: [0.7, 0.92], w: 1 },
];
/** bush archetype variants: weed clumps (same footprints as the default bushes) */
export const CITY_WEEDS: VariantDef[] = BUSH_VARIANTS.map((v) => ({ ...v }));

/** hazard tuning (exported for the tests and the renderer's set pieces) */
export const CITY_HAZ = {
  collapse: { n: 6, first: 70, gap: 21, every: [95, 125] as [number, number], warn: 2.8, power: 22, l: [5, 6.5] as [number, number], w: [2.3, 3] as [number, number] },
  cable: { n: 5, first: 40, gap: 9, every: [22, 28] as [number, number], warn: 2.2, active: 6, power: 4 },
  smoke: { n: 2, first: 130, gap: 45, every: 75, warn: 3, active: 20, power: 0.45, r: 8.5 },
} as const;

const dist = (x: number, z: number) => Math.hypot(x, z);
/** hazard counts were authored on the 110 m arena; they scale with the arena area (owner 2026-11-02: 160 m) */
export const cityHazardCounts = (arenaR: number): { collapse: number; cable: number; smoke: number } => {
  const k = Math.max(1, (arenaR / 110) ** 2);
  return { collapse: Math.round(CITY_HAZ.collapse.n * k), cable: Math.round(CITY_HAZ.cable.n * k), smoke: Math.round(CITY_HAZ.smoke.n * k) };
};

/** a tower drops a slab of its upper floors onto the street beside it (rectangle from the tower's foot, outward-ish) */
function collapses(rng: Rng, arenaR: number, props: readonly Prop[]): HazardDef[] {
  const H = CITY_HAZ.collapse, out: HazardDef[] = [];
  const spires = props.map((p, i) => ({ p, i })).filter(({ p }) => p.kind === 'spire' && dist(p.x, p.z) >= 22);
  // deterministic shuffle, then take the first that fit
  for (let k = spires.length - 1; k > 0; k--) { const j = rng.int(k + 1); [spires[k], spires[j]] = [spires[j], spires[k]]; }
  for (const { p, i } of spires) {
    if (out.length >= cityHazardCounts(arenaR).collapse) break;
    const l = rng.range(H.l[0], H.l[1]), w = rng.range(H.w[0], H.w[1]), out0 = Math.atan2(p.z, p.x);
    for (let t = 0; t < 8; t++) {
      const a = out0 + rng.range(-1.3, 1.3), c = Math.cos(a), s = Math.sin(a), d = p.r * 0.85 + l;
      const x = p.x + c * d, z = p.z + s * d;
      if (dist(x, z) - Math.hypot(l, w) < 13 || dist(x, z) + l > arenaR - 4) continue; // clear of the spawn, inside the wall
      // never drop a slab onto another tower
      if (props.some((q, j) => j !== i && q.kind === 'spire' && Math.hypot(q.x - x, q.z - z) < q.r + l * 0.8 + 1)) continue;
      const k = out.length;
      out.push({ kind: 'crush', x, z, r: 0, l, w, rot: a, start: H.first + k * H.gap + rng.range(0, 8), warn: H.warn, active: 0,
        every: rng.range(H.every[0], H.every[1]), power: H.power, tag: `collapse:${i}` });
      break;
    }
  }
  return out;
}

/** downed power lines: across a boulevard (kerb to kerb) or lying along one by the kerb, mid-block */
function cables(rng: Rng, arenaR: number, props: readonly Prop[]): HazardDef[] {
  const H = CITY_HAZ.cable, P = CITY.pitch, out: HazardDef[] = [], N = cityHazardCounts(arenaR).cable;
  // roads that cross the arena: |k| up to the last road line inside the wall; blocks along it likewise (2 / ±2 at 110 m)
  const KR = Math.max(2, Math.floor((arenaR - 10) / P)), MR = Math.max(2, Math.floor(arenaR / P) - 1);
  for (let tries = 0; out.length < N && tries < 80 * Math.ceil(N / H.n); tries++) {
    const k = (rng.int(2) ? 1 : -1) * (1 + rng.int(KR)), m = rng.int(2 * MR + 1) - MR, along = m * P + rng.range(11, P - 11);
    const uLine = rng.next() < 0.5, across = out.length % 2 === 0;
    const off = across ? 0 : (rng.next() < 0.5 ? -1 : 1) * (CITY.road - 2);
    const [x, z] = uLine ? toWorld(k * P + off, along) : toWorld(along, k * P + off);
    // u-lines run along v (heading + π/2); across a u-line road = along u (heading)
    const roadDir = uLine ? GRID_HEADING + Math.PI / 2 : GRID_HEADING, rot = across ? roadDir + Math.PI / 2 : roadDir;
    const l = across ? CITY.road + 1 : rng.range(6, 8);
    if (dist(x, z) < 22 || dist(x, z) > arenaR - 10) continue;
    if (props.some((q) => q.kind === 'spire' && Math.hypot(q.x - x, q.z - z) < q.r + l + 1)) continue;
    if (out.some((o) => Math.hypot(o.x - x, o.z - z) < 30)) continue;
    const i = out.length;
    out.push({ kind: 'burn', x, z, r: 0, l, w: 0.9, rot, start: H.first + i * H.gap + rng.range(0, 5), warn: H.warn, active: H.active,
      every: rng.range(H.every[0], H.every[1]), power: H.power, tag: 'cable' });
  }
  return out;
}

/** burning wrecks: two vehicle wrecks far apart flare up and pour smoke over the street now and then */
function smoke(rng: Rng, arenaR: number, props: readonly Prop[]): HazardDef[] {
  const H = CITY_HAZ.smoke, out: HazardDef[] = [];
  const wrecks = props.filter((p) => p.kind === 'boulder' && dist(p.x, p.z) >= 28 && dist(p.x, p.z) <= arenaR - 14);
  const N = cityHazardCounts(arenaR).smoke;
  for (let tries = 0; out.length < N && tries < 40 * N && wrecks.length; tries++) {
    const p = wrecks[rng.int(wrecks.length)];
    if (out.some((o) => Math.hypot(o.x - p.x, o.z - p.z) < 55)) continue;
    const i = out.length;
    out.push({ kind: 'fog', x: p.x, z: p.z, r: H.r, start: H.first + i * H.gap + rng.range(0, 10), warn: H.warn, active: H.active, every: H.every, power: H.power, tag: 'smoke' });
  }
  return out;
}

export const MAP: MapCoreDef = {
  id: 'shattered-metropolis',
  name: 'Shattered Metropolis',
  tagline: 'An abandoned city at dusk. Leaning towers shed their upper floors, downed power lines spark across the boulevards and smoke pours from the wrecks.',
  order: 2,
  status: 'ready',
  time: { default: 'day', day: 'dusk' },
  unlock: { after: 'canyon-meadow', seconds: 480 },
  terrain: {
    // low rubble swells under the streets (the city settled unevenly)
    hills: { ...CLASSIC_HILLS, mounds: 8, dip: 0.3, amp: [0.4, 1.1], dipAmp: [0.3, 0.6], swell: 0.2, max: 1.6, min: -0.8 },
    props: [
      { kind: 'spire', count: 9, rMin: 26, inset: 7, size: [3.4, 5.2], clear: 10, h: [11, 18], hpPer: 4.5 },
      { kind: 'boulder', count: 26, rMin: 13, inset: 3, size: [1.5, 2.4], clear: 3, h: [0.85, 1.2], hpPer: 2 },
      { kind: 'tree', count: 44, rMin: 13, inset: 2, size: [0.4, 0.4], clear: 2.2, h: [1, 1], variants: CITY_TREES },
      { kind: 'rock', count: 55, rMin: 8, inset: 2, size: [0.5, 0.9], clear: 1, h: [0.8, 1.2] },
      { kind: 'bush', count: 45, rMin: 9, inset: 2, size: [1, 1], clear: 0, h: [1, 1], variants: CITY_WEEDS, layout: 'clumps' },
    ],
  },
  fire: false,
  boss: { name: 'CURFEW BASTION', hpMul: 1.1 },
  hazards: ({ rng, arenaR, props }) => [...collapses(rng, arenaR, props), ...cables(rng, arenaR, props), ...smoke(rng, arenaR, props)],
};
